/**
 * Billing address rules — pure functions.
 *
 * Mirror of backend/src/core/payments/billing/billing-address.ts — keep in sync.
 * (Prisma/User-column helpers and gateway-only helpers are backend concerns and omitted.)
 */

export const BILLING_LIMITS = {
  name: { min: 1, max: 50 },
  line1: { min: 3, max: 50 },
  line2: { min: 0, max: 50 },
  city: { min: 3, max: 50 },
  zip: { min: 3, max: 10 },
  state: { min: 2, max: 3 },
} as const;

/** Countries where the gateway requires a state/province code. */
export const STATE_REQUIRED_COUNTRIES: readonly string[] = ['US', 'CA', 'AU'];

const COUNTRY_RE = /^[A-Z]{2}$/;
const STATE_RE = /^[A-Z0-9]{2,3}$/;
const ZIP_RE = /^[A-Za-z0-9][A-Za-z0-9 -]*[A-Za-z0-9]$/;
const NAME_RE = /^[\p{L}][\p{L} '’.-]*$/u;

export interface BillingAddress {
  firstName: string | null;
  lastName: string | null;
  line1: string | null;
  line2: string | null;
  city: string | null;
  zip: string | null;
  state: string | null;
  country: string | null;
}

export type BillingField = keyof BillingAddress;

export const BILLING_FIELDS: readonly BillingField[] = [
  'firstName', 'lastName', 'line1', 'line2', 'city', 'zip', 'state', 'country',
];

/** Trim and collapse whitespace; empty → null. */
export function normalise(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const s = String(value).replace(/\s+/g, ' ').trim();
  return s === '' ? null : s;
}

/**
 * Validate one field. Returns an error message, or null when valid.
 * An empty value is valid here — completeness is `missingFields`' job.
 */
export function fieldError(field: BillingField, value: string | null, country?: string | null): string | null {
  if (value === null) return null;
  const len = [...value].length;
  switch (field) {
    case 'firstName':
    case 'lastName':
      if (len > BILLING_LIMITS.name.max) return `Must be at most ${BILLING_LIMITS.name.max} characters`;
      return NAME_RE.test(value) ? null : 'Only letters, spaces, apostrophes, dots and hyphens';
    case 'line1':
    case 'line2':
    case 'city': {
      const lim = BILLING_LIMITS[field];
      return len < lim.min || len > lim.max ? `Must be ${lim.min}–${lim.max} characters` : null;
    }
    case 'zip':
      if (len < BILLING_LIMITS.zip.min || len > BILLING_LIMITS.zip.max) {
        return `Must be ${BILLING_LIMITS.zip.min}–${BILLING_LIMITS.zip.max} characters`;
      }
      return ZIP_RE.test(value) ? null : 'Only letters, digits, spaces and hyphens';
    case 'state':
      // Only meaningful where the gateway wants it; ignored everywhere else.
      if (!country || !STATE_REQUIRED_COUNTRIES.includes(country)) return null;
      return STATE_RE.test(value) ? null : 'Use the 2–3 character state/province code';
    case 'country':
      return COUNTRY_RE.test(value) ? null : 'Use a 2-letter ISO country code';
  }
}

/**
 * Normalise raw input: trims, upper-cases codes, drops unknown keys.
 * `undefined` = leave unchanged; `null`/'' = clear.
 */
export function normaliseInput(input: Partial<Record<BillingField, unknown>>): Partial<BillingAddress> {
  const out: Partial<BillingAddress> = {};
  for (const key of BILLING_FIELDS) {
    if (input[key] === undefined) continue;
    let v = normalise(input[key]);
    if (v && (key === 'country' || key === 'state')) v = v.toUpperCase();
    out[key] = v;
  }
  return out;
}

/** Validate a full address. Returns `{field: message}`; empty object = valid. */
export function validate(addr: BillingAddress): Partial<Record<BillingField, string>> {
  const errors: Partial<Record<BillingField, string>> = {};
  for (const key of BILLING_FIELDS) {
    const err = fieldError(key, addr[key], addr.country);
    if (err) errors[key] = err;
  }
  return errors;
}

/** Fields that must be filled before the address can be sent to the gateway. */
export function missingFields(addr: BillingAddress): BillingField[] {
  const required: BillingField[] = ['firstName', 'lastName', 'line1', 'city', 'zip', 'country'];
  if (addr.country && STATE_REQUIRED_COUNTRIES.includes(addr.country)) required.push('state');
  return required.filter((f) => !addr[f]);
}

export function isComplete(addr: BillingAddress): boolean {
  return missingFields(addr).length === 0 && Object.keys(validate(addr)).length === 0;
}

/** One-line address for display: "Street 1, 10115 Berlin, DE". */
export function formatOneLine(addr: BillingAddress): string {
  return [addr.line1, addr.line2, [addr.zip, addr.city].filter(Boolean).join(' '), addr.country]
    .filter((p) => p && String(p).trim())
    .join(', ');
}
